import React, { useState, useEffect } from 'react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import './App.css';
import './styles/ocean-shimmer.css';
import './i18n'; // i18n multi-language support

// Import refactored modules
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider, useTheme, ThemeToggle } from './contexts/ThemeContext';
import { AnalyticsCacheProvider } from './contexts/AnalyticsCacheContext';
import { FilterProvider, useFilters } from './contexts/FilterContext';
import { API } from './utils/api';
import { setProhibitedTopics } from './utils/prohibitedFilter';
import { Toast, Sidebar, QuoteOfTheDay, BookPromoBanner, JokeOfTheDay } from './components/shared';
import AINewsTicker from './components/shared/AINewsTicker';
import { LaughProvider } from './components/Gamification/LaughOMeter';
import { FloatingEasterEggsController } from './components/Gamification/FloatingEasterEggs';
import { InAppBrowserWarning } from './utils/facebookBrowser';
import { 
  LoginPage, 
  RegisterPage, 
  AuthCallback, 
  AdminPanel, 
  SettingsPage, 
  SubscribePage, 
  MessagesPage,
  UltimateSearchPage,
  SocialPage,
  MapPage,
  MarketplacePage,
  AchievementsPage,
  QuoteGalleryPage,
  AnalyticsPage
} from './pages';
import ChatPage from './pages/ChatPage';
import AdvancedAnalytics from './components/AdvancedAnalytics';
import StatisticsPage from './pages/StatisticsPage';
import TutorialsPage from './pages/TutorialsPage';
import PersonalReportsPage from './pages/PersonalReportsPage';
import PayPalWalletPage from './pages/PayPalWalletPage';
import LegalPage from './pages/LegalPage';
import TemplatesPage from './pages/TemplatesPage';
import DonationPage from './pages/DonationPage';
import TaskHistoryPage from './pages/TaskHistoryPage';
import SearchAnalyticsPage from './pages/SearchAnalyticsPage';
import UserProfilePage from './pages/UserProfilePage';
import AdvancedAnalyticsDashboardPage from './pages/AdvancedAnalyticsDashboardPage';
import CollaborationPage from './pages/CollaborationPage';
import VersionChecker from './components/VersionChecker';

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// ==================== MAIN APP ====================
const MainApp = () => {
  const { user, token } = useAuth();
  const { loadPreferences } = useFilters();
  const [currentPage, setCurrentPage] = useState('search');
  const [toast, setToast] = useState(null);

  // Auto-load saved filter preferences on login
  useEffect(() => {
    if (!token) return;
    const x = new XMLHttpRequest();
    x.open('GET', `${API}/filter-preferences`, true);
    x.setRequestHeader('Authorization', `Bearer ${token}`);
    x.onload = function() {
      if (x.status >= 200 && x.status < 300) {
        try {
          const prefs = JSON.parse(x.responseText);
          if (prefs && Object.keys(prefs).length > 0) {
            loadPreferences(prefs);
          }
        } catch (e) { /* no saved prefs */ }
      }
    };
    x.send();
  }, [token, loadPreferences]);

  // Fetch admin-editable prohibited-topics list at boot + poll for live updates
  // every 30 seconds. Cheap version endpoint minimizes bandwidth.
  useEffect(() => {
    let cancelled = false;
    let timer;
    let currentVersion = null;

    const fetchFull = async () => {
      try {
        const r = await fetch(`${API}/prohibited-topics`);
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled && Array.isArray(data.topics)) {
          setProhibitedTopics(data.topics);
          currentVersion = data.version;
        }
      } catch { /* fallback to defaults */ }
    };

    const checkVersion = async () => {
      try {
        const r = await fetch(`${API}/prohibited-topics/version`);
        if (!r.ok) return;
        const { version } = await r.json();
        if (!cancelled && version && version !== currentVersion) {
          await fetchFull();
        }
      } catch { /* silent */ }
      if (!cancelled) {
        timer = setTimeout(checkVersion, 30000);
      }
    };

    // Initial load, then start polling
    fetchFull().then(() => {
      if (!cancelled) timer = setTimeout(checkVersion, 30000);
    });

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  const showToast = (message, type) => {
    setToast({ message, type });
  };

  // Handle page navigation
  const handlePageChange = (pageId) => {
    setCurrentPage(pageId);
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'admin':
        return user?.is_admin ? <AdminPanel showToast={showToast} /> : <UltimateSearchPage showToast={showToast} />;
      case 'analytics':
        return <AnalyticsPage showToast={showToast} />;
      case 'statistics':
        return <StatisticsPage showToast={showToast} />;
      case 'search':
        return <UltimateSearchPage showToast={showToast} />;
      case 'marketplace':
        return <MarketplacePage showToast={showToast} />;
      case 'achievements':
        return <AchievementsPage showToast={showToast} />;
      case 'social':
        return <SocialPage showToast={showToast} />;
      case 'map':
        return <MapPage showToast={showToast} setCurrentPage={setCurrentPage} />;
      case 'quotes':
        return <QuoteGalleryPage showToast={showToast} />;
      case 'messages':
        return <MessagesPage showToast={showToast} />;
      case 'cockpit':
      case 'chat':
        return <ChatPage showToast={showToast} />;
      case 'tutorials':
        return <TutorialsPage showToast={showToast} />;
      case 'settings':
        return <SettingsPage showToast={showToast} setCurrentPage={setCurrentPage} />;
      case 'subscribe':
        return <DonationPage showToast={showToast} onBack={() => setCurrentPage('settings')} />;
      case 'personal-reports':
        return <PersonalReportsPage showToast={showToast} onBack={() => setCurrentPage('settings')} />;
      case 'wallet':
        return <PayPalWalletPage showToast={showToast} onBack={() => setCurrentPage('settings')} />;
      case 'legal':
        return <LegalPage showToast={showToast} />;
      case 'templates':
        return <TemplatesPage token={token} showToast={showToast} />;
      case 'donation':
        return <DonationPage showToast={showToast} />;
      case 'donation-success':
        return <DonationPage showToast={showToast} isSuccessPage={true} />;
      case 'task-history':
        return <TaskHistoryPage showToast={showToast} />;
      case 'search-analytics':
        return <SearchAnalyticsPage showToast={showToast} />;
      case 'profile':
        return <UserProfilePage showToast={showToast} onBack={() => setCurrentPage('search')} />;
      case 'view-profile':
        return <UserProfilePage showToast={showToast} userId={window.__viewProfileUserId} onBack={() => setCurrentPage('chat')} />;
      case 'advanced-analytics':
        return <AdvancedAnalyticsDashboardPage showToast={showToast} />;
      case 'collaboration':
        return <CollaborationPage showToast={showToast} />;
      default:
        return <UltimateSearchPage showToast={showToast} />;
    }
  };

  // Pages that should have minimal headers (admin/analytics/utility pages)
  const isAdminPage = ['admin', 'analytics', 'legal', 'wallet', 'personal-reports', 'subscribe', 'search-analytics', 'task-history'].includes(currentPage);
  // Pages where AI News should show
  const showAiNews = ['search', 'marketplace', 'social'].includes(currentPage);
  
  // State for in-app browser warning
  const [dismissedInAppWarning, setDismissedInAppWarning] = useState(false);
  
  return (
    <div className="app-container">
      <Sidebar currentPage={currentPage} setCurrentPage={handlePageChange} showToast={showToast} />
      <main className="main-content pt-16 md:pt-0">
        {/* Book Promotion Banner - Hide on admin/analytics pages for cleaner UX */}
        {!isAdminPage && <BookPromoBanner />}
        {/* Quote of the Day - Hide on admin/analytics pages */}
        {!isAdminPage && <QuoteOfTheDay />}
        {/* Joke of the Day — witty humor from Letters to Evelyn */}
        {!isAdminPage && <JokeOfTheDay />}
        {renderPage()}
      </main>
      {/* AI News Ticker - Shows on main pages */}
      {showAiNews && <AINewsTicker />}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      {/* Floating Easter Eggs - Respects user's frequency preference */}
      <FloatingEasterEggsController
        enabled={localStorage.getItem('easter_egg_enabled') !== 'false'}
        frequency={parseInt(localStorage.getItem('easter_egg_frequency') || '45000')}
      />
      {/* Facebook/In-App Browser Warning */}
      {!dismissedInAppWarning && (
        <InAppBrowserWarning onDismiss={() => setDismissedInAppWarning(true)} />
      )}
    </div>
  );
};

// ==================== APP ROOT ====================
function App() {
  const [authMode, setAuthMode] = useState('login');

  return (
    <AuthProvider>
      <ThemeProvider>
        <AnalyticsCacheProvider>
          <FilterProvider>
            <LaughProvider>
              <AppContent authMode={authMode} setAuthMode={setAuthMode} />
              <VersionChecker />
            </LaughProvider>
          </FilterProvider>
        </AnalyticsCacheProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

const AppContent = ({ authMode, setAuthMode }) => {
  const { user, loading } = useAuth();

  // Check for session_id in URL hash OR query params (Google OAuth callback)
  // Mobile browsers may place session_id in query params instead of hash
  if (window.location.hash?.includes('session_id=') || 
      window.location.search?.includes('session_id=') ||
      window.location.href?.match(/session_id=[^&#]+/)) {
    return <AuthCallback />;
  }

  if (loading) {
    return (
      <div className="auth-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (!user) {
    return authMode === 'login' 
      ? <LoginPage onSwitch={() => setAuthMode('register')} />
      : <RegisterPage onSwitch={() => setAuthMode('login')} />;
  }

  return <MainApp />;
};

export default App;
